import { ReactNode, useEffect, useRef, useState } from 'react';
import { Table, TableContainer } from 'styles';
import palette from 'styles/theme/Palette';

interface ITableList {
	header: ReactNode;
	body?: ReactNode;
	stickyHeader?: boolean;
}

const TableList = ({ header, body, stickyHeader = true }: ITableList) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [isScrollable, setIsScrollable] = useState(false);

	// The sticky-edge line is shown only when the table really scrolls horizontally
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;
		const update = () =>
			setIsScrollable(container.scrollWidth > container.clientWidth);
		update();
		const observer = new ResizeObserver(update);
		observer.observe(container);
		if (container.firstElementChild) observer.observe(container.firstElementChild);
		return () => observer.disconnect();
	}, []);

	return (
		<TableContainer ref={containerRef}>
			<Table
				stickyHeader={stickyHeader}
				sx={{
					borderCollapse: 'separate',
					borderSpacing: '0 8px',
					marginTop: '-8px',
					'& th': {
						height: 66,
						padding: '0px 0px 0px 10px!important',
						'&:first-of-type': stickyHeader
							? {
									position: 'sticky',
									left: 0,
									top: 0,
									zIndex: 10,
								}
							: {},
						'&:last-of-type': {
							padding: '0 10px 0 0!important',
							...(stickyHeader
								? {
										position: 'sticky',
										right: 0,
										top: 0,
										padding: '0!important',
										textAlign: 'center',
										minWidth: 56,
									}
								: {}),
						},
					},
					'& td': {
						backgroundColor: 'white!important',
						height: 54,
						padding: '0 0 0 10px',
						'&:first-of-type': {
							borderTopLeftRadius: '4px',
							borderBottomLeftRadius: '4px',
							...(stickyHeader ? { position: 'sticky', left: 0, top: 0 } : {}),
						},
						'&:last-of-type': {
							textAlign: 'right',
							borderTopRightRadius: '4px',
							borderBottomRightRadius: '4px',
							padding: '0 10px 0 0!important',
							...(stickyHeader
								? {
										position: 'sticky',
										right: 0,
										top: 0,
										padding: '0!important',
										textAlign: 'center',
										minWidth: 56,
									}
								: {}),
						},
					},
					// Righetta verticale 1px sul bordo delle colonne sticky (solo se la tabella scorre)
					...(stickyHeader && isScrollable
						? {
								'& td:first-of-type::after, & td:last-of-type::after': {
									content: '""',
									position: 'absolute',
									top: 0,
									bottom: 0,
									width: '1px',
									backgroundColor: palette.grey4.main,
								},
								'& td:first-of-type::after': {
									left: '100%',
								},
								'& td:last-of-type::after': {
									right: '100%',
								},
							}
						: {}),
				}}
			>
				{header}
				{body}
			</Table>
		</TableContainer>
	);
};

export default TableList;
